import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Badge,
  Avatar,
  Table,
  ThemeIcon,
  Progress,
  Divider
} from '@mantine/core';
import {
  IconTrophy,
  IconMedal,
  IconClock,
  IconTarget,
  IconHome,
  IconRefresh,
  IconUsers
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { 
  RankingSalaDTO, 
  SalaMultijugadorResponseDTO,
  ParticipanteSalaDTO 
} from '../../types/multijugador';
import { MultijugadorService } from '../../services/multijugadorService';
import { WebSocketService } from '../../services/webSocketService';

interface RankingMultijugadorProps {
  codigoSala: string;
  sala: SalaMultijugadorResponseDTO;
}

const RankingMultijugador: React.FC<RankingMultijugadorProps> = ({
  codigoSala,
  sala
}) => {
  const navigate = useNavigate();
  const [ranking, setRanking] = useState<RankingSalaDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [primeraLlamada, setPrimeraLlamada] = useState(true);
  const webSocketService = WebSocketService.getInstance();

  useEffect(() => {
    cargarRanking();
    configurarWebSocket();

    return () => {
      webSocketService.desuscribirseDeASala(codigoSala);
    };
  }, [codigoSala]);

  const cargarRanking = async (mostrarNotificacionError: boolean = false) => {
    try {
      setLoading(true);
      const rankingData = await MultijugadorService.obtenerRanking(codigoSala);
      setRanking(rankingData);
      setPrimeraLlamada(false); // Marcar que ya se cargó al menos una vez
    } catch (error: any) {
      console.error('Error al cargar ranking:', error);
      // Solo mostrar notificación si NO es la primera carga o si se solicita explícitamente
      if (!primeraLlamada || mostrarNotificacionError) {
        notifications.show({
          title: 'Error al cargar ranking',
          message: error.message,
          color: 'red',
        });
      }
    } finally {
      setLoading(false);
      setPrimeraLlamada(false); // Marcar como cargado incluso si falla
    }
  };

  const configurarWebSocket = async () => {
    try {
      await webSocketService.connect();

      webSocketService.suscribirseASala(codigoSala, {
        onActualizacionRanking: (nuevoRanking: RankingSalaDTO) => {
          setRanking(nuevoRanking);
        }
      });
    } catch (error) {
      console.error('Error al conectar WebSocket:', error);
    }
  };

  const getPosicionIcon = (posicion: number) => {
    switch (posicion) {
      case 1:
        return <IconTrophy size={24} className="text-yellow-500" />;
      case 2:
        return <IconMedal size={24} className="text-gray-400" />;
      case 3:
        return <IconMedal size={24} className="text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-gray-600">#{posicion}</span>;
    }
  };

  const getPosicionColor = (posicion: number) => {
    switch (posicion) {
      case 1: return 'yellow';
      case 2: return 'gray';
      case 3: return 'orange';
      default: return 'blue';
    }
  };

  const formatearTiempo = (segundos: number) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  if (loading || !ranking) {
    return (
      <Container size="lg" py="xl">
        <div className="flex justify-center items-center h-64">
          <Stack align="center" gap="md">
            <ThemeIcon size={50} variant="light" color="blue">
              <IconTrophy size={30} />
            </ThemeIcon>
            <Text>Cargando resultados...</Text>
          </Stack>
        </div>
      </Container>
    );
  }

  const participantesTerminados = ranking.ranking.filter(p => p.finalizoExamen);
  const progreso = (participantesTerminados.length / ranking.totalParticipantes) * 100;

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <div className="text-center">
          <Group justify="center" gap="sm" mb="md">
            <ThemeIcon size={50} variant="gradient" gradient={{ from: 'yellow', to: 'orange' }}>
              <IconTrophy size={30} />
            </ThemeIcon>
            <Title order={1} className="text-3xl font-bold text-yellow-600">
              <Group gap={10} justify="center" align="center">
                <IconTrophy size={30} />
                Ranking Final
              </Group>
            </Title>
          </Group>
          
          <Text size="lg" className="text-gray-600 mb-2">
            {ranking.nombreSala}
          </Text>
          
          <Badge size="lg" variant="light" color="blue">
            Código: {ranking.codigoSala}
          </Badge>
        </div>

        {/* Progreso */}
        {!ranking.salaFinalizada && (
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="md">
              <Text fw={500}>Progreso del examen</Text>
              <Badge variant="light" color="blue">
                {participantesTerminados.length}/{ranking.totalParticipantes} terminados
              </Badge>
            </Group>
            <Progress value={progreso} color="blue" size="lg" />
          </Card>
        )}

        {/* Podio (Top 3) */}
        {participantesTerminados.length > 0 && (
          <Card withBorder padding="xl" className="bg-gradient-to-br from-yellow-50 to-orange-50">
            <Title order={2} className="text-center mb-lg">
              <Group gap={10} justify="center" align="center">
                <IconMedal size={26} />
                Podio de Ganadores
              </Group>
            </Title>
            
            <div className="grid md:grid-cols-3 gap-4">
              {participantesTerminados.slice(0, 3).map((participante, index) => {
                const posicion = index + 1;
                return (
                  <Card 
                    key={participante.id} 
                    withBorder 
                    padding="lg" 
                    className={`text-center ${
                      posicion === 1 ? 'bg-yellow-100 border-yellow-300' :
                      posicion === 2 ? 'bg-gray-100 border-gray-300' :
                      'bg-orange-100 border-orange-300'
                    }`}
                  >
                    <Stack align="center" gap="md">
                      <div className="relative">
                        <Avatar
                          src={participante.avatarUrl}
                          size="xl"
                          color={getPosicionColor(posicion)}
                          className={posicion === 1 ? 'ring-4 ring-yellow-400' : ''}
                        >
                          {participante.nombreUsuario.charAt(0).toUpperCase()}
                        </Avatar>
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                          {getPosicionIcon(posicion)}
                        </div>
                      </div>
                      
                      <div>
                        <Text fw={700} size="lg" className={
                          posicion === 1 ? 'text-yellow-700' :
                          posicion === 2 ? 'text-gray-700' :
                          'text-orange-700'
                        }>
                          {participante.nombreUsuario}
                        </Text>
                        <Text fw={600} size="xl" color={getPosicionColor(posicion)}>
                          {participante.puntuacion?.toFixed(1)} pts
                        </Text>
                        <Text size="sm" color="dimmed">
                          {formatearTiempo(participante.tiempoTotalSegundos || 0)}
                        </Text>
                      </div>
                    </Stack>
                  </Card>
                );
              })}
            </div>
          </Card>
        )}

        {/* Tabla completa */}
        <Card withBorder padding="lg">
          <Title order={3} mb="md" className="flex items-center gap-2">
            <IconUsers size={24} />
            Resultados Completos
          </Title>
          
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Posición</Table.Th>
                <Table.Th>Jugador</Table.Th>
                <Table.Th>Puntuación</Table.Th>
                <Table.Th>Tiempo</Table.Th>
                <Table.Th>Aciertos</Table.Th>
                <Table.Th>Errores</Table.Th>
                <Table.Th>En Blanco</Table.Th>
                <Table.Th>Estado</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ranking.ranking.map((participante, index) => (
                <Table.Tr key={participante.id}>
                  <Table.Td>
                    <Group gap="xs">
                      {participante.posicionFinal ? (
                        <>
                          {getPosicionIcon(participante.posicionFinal)}
                          <Badge 
                            variant="light" 
                            color={getPosicionColor(participante.posicionFinal)}
                            size="sm"
                          >
                            {participante.posicionFinal}°
                          </Badge>
                        </>
                      ) : (
                        <Badge variant="light" color="gray" size="sm">
                          En curso
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar
                        src={participante.avatarUrl}
                        size="sm"
                        color="blue"
                      >
                        {participante.nombreUsuario.charAt(0).toUpperCase()}
                      </Avatar>
                      <div>
                        <Text fw={500} size="sm">
                          {participante.nombreUsuario}
                        </Text>
                        {participante.esCreador && (
                          <Badge variant="filled" color="gold" size="xs">
                            Creador
                          </Badge>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  
                  <Table.Td>
                    <Text fw={600} color={participante.finalizoExamen ? 'blue' : 'dimmed'}>
                      {participante.puntuacion?.toFixed(1) || '-'} pts
                    </Text>
                  </Table.Td>
                  
                  <Table.Td>
                    <Group gap="xs">
                      <IconClock size={14} />
                      <Text size="sm">
                        {participante.tiempoTotalSegundos 
                          ? formatearTiempo(participante.tiempoTotalSegundos)
                          : '-'
                        }
                      </Text>
                    </Group>
                  </Table.Td>
                  
                  <Table.Td>
                    <Text color="green" fw={500}>
                      {participante.preguntasCorrectas || 0}
                    </Text>
                  </Table.Td>
                  
                  <Table.Td>
                    <Text color="red">
                      {participante.preguntasIncorrectas || 0}
                    </Text>
                  </Table.Td>
                  
                  <Table.Td>
                    <Text color="orange">
                      {participante.preguntasEnBlanco || 0}
                    </Text>
                  </Table.Td>
                  
                  <Table.Td>
                    <Badge 
                      variant="light" 
                      color={participante.finalizoExamen ? 'green' : 'blue'}
                      size="sm"
                    >
                      {participante.finalizoExamen ? 'Terminado' : 'En progreso'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        {/* Acciones */}
        <Group justify="center" gap="md">
          <Button
            variant="outline"
            leftSection={<IconRefresh size={18} />}
            onClick={() => { cargarRanking(true); }}
            loading={loading}
          >
            Actualizar
          </Button>
          
          <Button
            variant="gradient"
            gradient={{ from: 'blue', to: 'cyan' }}
            leftSection={<IconHome size={18} />}
            onClick={() => navigate('/multijugador')}
          >
            Volver al Inicio
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default RankingMultijugador;